import type { LngLat, RegionFill } from "./types.ts";

export type { RegionFill };

export interface PlanarFace {
  cycle: number[];
  key: string;
  area: number;
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
}

export const FILL_PALETTE_IDS = ["default", "pastel", "vivid", "earth", "mono"] as const;
export type FillPaletteId = (typeof FILL_PALETTE_IDS)[number];

/** Ten hues across, four lightness steps down. */
export const PALETTE_COLUMNS = 10;
export const PALETTE_SWATCH_COUNT = 40;
/** Shared by every palette set. Empty slots stay empty until the user assigns one. */
export const CUSTOM_SLOT_COUNT = 10;
/** Choosing this swatch clears the face instead of painting a color. */
export const NO_FILL = "none";

/**
 * Appended to every palette set for fill and line pickers (not part of the 40-step ramps).
 * White is kept for light strokes on dark basemaps; it is not generated by the fill ramps.
 */
export const SHARED_EXTRA_SWATCHES: readonly string[] = ["#ffffff"];

/** Swatches shown in both fill and stroke pickers for one palette set. */
export function sharedPaletteSwatches(id: FillPaletteId): readonly string[] {
  return [...FILL_SWATCHES[id], ...SHARED_EXTRA_SWATCHES];
}

export const SHARED_PALETTE_SWATCH_COUNT = PALETTE_SWATCH_COUNT + SHARED_EXTRA_SWATCHES.length;

const SPECTRUM = [2, 22, 42, 68, 128, 162, 190, 214, 258, 328];
const EARTH_HUES = [16, 28, 40, 54, 78, 104, 132, 158, 188, 214];
const MONO_HUES = [18, 42, 72, 118, 152, 188, 214, 252, 292, 338];

/** Light on the top row, dark on the bottom. Each column is one hue. */
const TONE_STEPS: Record<FillPaletteId, ReadonlyArray<readonly [number, number]>> = {
  default: [
    [46, 90],
    [74, 66],
    [80, 46],
    [62, 28],
  ],
  pastel: [
    [32, 94],
    [42, 84],
    [48, 74],
    [44, 64],
  ],
  vivid: [
    [90, 60],
    [96, 46],
    [94, 34],
    [82, 22],
  ],
  earth: [
    [28, 84],
    [38, 62],
    [36, 42],
    [26, 24],
  ],
  mono: [
    [18, 92],
    [22, 70],
    [24, 48],
    [18, 26],
  ],
};

export const FILL_SWATCHES: Record<FillPaletteId, readonly string[]> = {
  default: paletteRamp(SPECTRUM, TONE_STEPS.default),
  pastel: paletteRamp(SPECTRUM, TONE_STEPS.pastel),
  vivid: paletteRamp(SPECTRUM, TONE_STEPS.vivid),
  earth: paletteRamp(EARTH_HUES, TONE_STEPS.earth),
  mono: paletteRamp(MONO_HUES, TONE_STEPS.mono),
};

/** A mid blue: hue column 7, second lightness row. */
export const DEFAULT_FILL_COLOR = FILL_SWATCHES.default[PALETTE_COLUMNS + 7] ?? "#3b82f6";

export type PaletteCell =
  | { kind: "none" }
  | { kind: "swatch"; color: string }
  | { kind: "custom"; slot: number; color: string | null };

export interface PaletteCellsOptions {
  /** When false, omit the No fill cell (stroke pickers). Default true. */
  includeNone?: boolean;
  /** When set, append this color if it is not already in the grid (saved custom strokes). */
  ensureColor?: string | null;
}

/** No fill (optional), set swatches + shared extras, then the shared custom slots. */
export function paletteCells(
  id: FillPaletteId,
  customs: readonly (string | null)[],
  options?: PaletteCellsOptions,
): PaletteCell[] {
  const includeNone = options?.includeNone !== false;
  const cells: PaletteCell[] = includeNone ? [{ kind: "none" }] : [];
  for (const color of sharedPaletteSwatches(id)) cells.push({ kind: "swatch", color });
  for (let slot = 0; slot < CUSTOM_SLOT_COUNT; slot++) {
    const color = customs[slot] ?? null;
    cells.push({ kind: "custom", slot, color });
  }
  const ensure = options?.ensureColor;
  if (ensure && ensure !== NO_FILL) {
    const hex = normalizeColor(ensure) ?? ensure.toLowerCase();
    if (hex && !paletteContainsColor(cells, hex)) cells.push({ kind: "swatch", color: hex });
  }
  return cells;
}

export function paletteContainsColor(cells: readonly PaletteCell[], color: string): boolean {
  const hex = normalizeColor(color) ?? color.toLowerCase();
  for (const cell of cells) {
    if (cell.kind === "none") continue;
    if (cell.kind === "swatch" && cell.color === hex) return true;
    if (cell.kind === "custom" && cell.color === hex) return true;
  }
  return false;
}

export function paletteSelectionIndex(
  color: string,
  id: FillPaletteId,
  customs: readonly (string | null)[],
  options?: PaletteCellsOptions,
): number {
  const cells = paletteCells(id, customs, options);
  const target = color === NO_FILL ? NO_FILL : (normalizeColor(color) ?? color.toLowerCase());
  for (let index = 0; index < cells.length; index++) {
    const cell = cells[index];
    if (!cell) continue;
    if (target === NO_FILL) {
      if (cell.kind === "none") return index;
      continue;
    }
    if (cell.kind === "swatch" && cell.color === target) return index;
    if (cell.kind === "custom" && cell.color === target) return index;
  }
  return includeNoneOffset(options);
}

function includeNoneOffset(options?: PaletteCellsOptions): number {
  return options?.includeNone === false ? 0 : 1;
}

/**
 * Reads custom colors, including an older 20-slot list.
 * Empty holes are skipped and the first assigned colors are kept, packed to the front.
 */
export function parseCustomSlots(raw: string | null): (string | null)[] {
  const out: (string | null)[] = Array.from({ length: CUSTOM_SLOT_COUNT }, () => null);
  if (!raw) return out;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return out;
  }
  if (!Array.isArray(parsed)) return out;
  const assigned: string[] = [];
  for (const item of parsed) {
    if (typeof item !== "string") continue;
    const color = normalizeColor(item);
    if (!color) continue;
    assigned.push(color);
    if (assigned.length === CUSTOM_SLOT_COUNT) break;
  }
  for (let i = 0; i < assigned.length; i++) out[i] = assigned[i] ?? null;
  return out;
}

/**
 * MapLibre antialiases a fill by pulling the opaque edge inside the polygon,
 * which can leave a gap inside the stroke. The fill layer turns antialiasing
 * off and sits under the colored line so the polygon meets the centerline.
 * The ring uses the vertex coordinates with no inset.
 */
export const REGION_FILL_ANTIALIAS = false;

export interface RegionFeature {
  type: "Feature";
  properties: { color: string };
  geometry: { type: "Polygon"; coordinates: LngLat[][] };
}

/**
 * Remembers the GeoJSON last sent to the fill source.
 * `null` means the source is stale, including after undo back to no fill.
 * An empty paint string means an empty collection was actually written.
 */
export class RegionLayerSync {
  private paint: string | null = null;

  invalidate(): void {
    this.paint = null;
  }

  update(
    regions: readonly RegionFill[],
    points: readonly LngLat[],
  ): { type: "FeatureCollection"; features: RegionFeature[] } | null {
    const next = regionFeatures(regions, points);
    if (next.paint === this.paint) return null;
    this.paint = next.paint;
    return { type: "FeatureCollection", features: next.features };
  }
}

export function regionFeatures(
  regions: readonly RegionFill[],
  points: readonly LngLat[],
): { paint: string; features: RegionFeature[] } {
  const features: RegionFeature[] = [];
  let paint = "";
  for (const region of regions) {
    const ring: LngLat[] = [];
    let ok = true;
    for (const id of region.cycle) {
      const point = points[id];
      if (!point) {
        ok = false;
        break;
      }
      ring.push([point[0], point[1]]);
    }
    if (!ok || ring.length < 3) continue;
    paint += `${region.color}|`;
    for (const point of ring) paint += `${point[0].toFixed(5)},${point[1].toFixed(5)};`;
    const first = ring[0];
    features.push({
      type: "Feature",
      properties: { color: region.color },
      geometry: { type: "Polygon", coordinates: [first ? [...ring, first] : ring] },
    });
  }
  return { paint, features };
}

function paletteRamp(hues: readonly number[], steps: ReadonlyArray<readonly [number, number]>): string[] {
  const out: string[] = [];
  for (const step of steps) {
    for (const hue of hues) out.push(hslToHex(hue, step[0], step[1]));
  }
  return out;
}

function hslToHex(hue: number, saturation: number, lightness: number): string {
  const sat = saturation / 100;
  const lig = lightness / 100;
  const chroma = (1 - Math.abs(2 * lig - 1)) * sat;
  const hp = (((hue % 360) + 360) % 360) / 60;
  const x = chroma * (1 - Math.abs((hp % 2) - 1));
  let red = 0;
  let green = 0;
  let blue = 0;
  if (hp < 1) [red, green, blue] = [chroma, x, 0];
  else if (hp < 2) [red, green, blue] = [x, chroma, 0];
  else if (hp < 3) [red, green, blue] = [0, chroma, x];
  else if (hp < 4) [red, green, blue] = [0, x, chroma];
  else if (hp < 5) [red, green, blue] = [x, 0, chroma];
  else [red, green, blue] = [chroma, 0, x];
  const match = lig - chroma / 2;
  const channel = (value: number) => Math.round((value + match) * 255).toString(16).padStart(2, "0");
  return `#${channel(red)}${channel(green)}${channel(blue)}`;
}

const METERS_PER_DEG = 111_320;
/** Faces smaller than this are spikes and bridges, not regions. */
const MIN_FACE_M2 = 0.05;

interface Half {
  to: number;
  angle: number;
}

/** `#rrggbb`, or null when the text is not a color. */
export function normalizeColor(value: string): string | null {
  const text = value.trim().toLowerCase();
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(text);
  if (!match) return null;
  const hex = match[1] ?? "";
  if (hex.length === 3) {
    return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }
  return `#${hex}`;
}

export function isFillPaletteId(value: string): value is FillPaletteId {
  return (FILL_PALETTE_IDS as readonly string[]).includes(value);
}

/**
 * Bounded faces of an undirected line graph. Crossings are already split, so
 * each edge is a straight side. The unbounded outer face is the clockwise
 * walk and is dropped. A bridge or a dangling line has no area and is dropped.
 */
export function planarFaces(
  points: readonly LngLat[],
  segments: ReadonlyArray<readonly [number, number]>,
): PlanarFace[] {
  const n = points.length;
  if (n === 0 || segments.length === 0) return [];
  const adj: Half[][] = Array.from({ length: n }, () => []);
  for (const [a, b] of segments) {
    if (!Number.isInteger(a) || !Number.isInteger(b)) continue;
    if (a === b || a < 0 || b < 0 || a >= n || b >= n) continue;
    const pa = points[a];
    const pb = points[b];
    if (!pa || !pb) continue;
    adj[a].push({ to: b, angle: angleBetween(pa, pb) });
    adj[b].push({ to: a, angle: angleBetween(pb, pa) });
  }
  for (const list of adj) list.sort((x, y) => x.angle - y.angle || x.to - y.to);
  const used: boolean[][] = adj.map((list) => list.map(() => false));
  const faces: PlanarFace[] = [];
  const seen = new Set<string>();
  const limit = segments.length * 2 + 1;
  for (let u = 0; u < n; u++) {
    for (let e = 0; e < adj[u].length; e++) {
      if (used[u][e]) continue;
      const cycle = walkFace(adj, used, u, e, limit);
      if (cycle.length < 3) continue;
      const area = signedAreaM2(points, cycle);
      if (area <= MIN_FACE_M2) continue;
      const canonical = rotateMin(cycle);
      const key = canonical.join(",");
      if (seen.has(key)) continue;
      seen.add(key);
      faces.push({ cycle: canonical, key, area, ...bboxOf(points, canonical) });
    }
  }
  return faces;
}

/** Point moves keep the cycles and change the boxes the click test uses. */
export function refreshFaceBounds(faces: readonly PlanarFace[], points: readonly LngLat[]): void {
  for (const face of faces) {
    const box = bboxOf(points, face.cycle);
    face.minLng = box.minLng;
    face.minLat = box.minLat;
    face.maxLng = box.maxLng;
    face.maxLat = box.maxLat;
  }
}

/**
 * One fill per closed face, all the same color.
 * Each seed sits in that face, including when a smaller face sits inside it.
 */
export function paintAllFaces(
  points: readonly LngLat[],
  faces: readonly PlanarFace[],
  color: string,
): RegionFill[] {
  return faces.map((face) => ({
    cycle: face.cycle.slice(),
    color,
    seed: interiorSeed(points, face.cycle, faces),
  }));
}

/** Every face already wears `color`. A null color means nothing is filled. */
export function facesShareFill(
  regions: readonly RegionFill[],
  faces: readonly PlanarFace[],
  color: string | null,
): boolean {
  if (color == null) return regions.length === 0;
  if (faces.length === 0 || regions.length !== faces.length) return false;
  const colors = new Map<string, string>();
  for (const region of regions) colors.set(region.cycle.join(","), region.color);
  if (colors.size !== faces.length) return false;
  for (const face of faces) {
    if (colors.get(face.key) !== color) return false;
  }
  return true;
}

/** Smallest bounded face that contains the point. A point on the boundary counts. */
export function faceAt(
  faces: readonly PlanarFace[],
  points: readonly LngLat[],
  lng: number,
  lat: number,
): PlanarFace | null {
  let best: PlanarFace | null = null;
  for (const face of faces) {
    if (lng < face.minLng || lng > face.maxLng || lat < face.minLat || lat > face.maxLat) continue;
    if (!ringContains(points, face.cycle, lng, lat)) continue;
    if (!best || face.area < best.area) best = face;
  }
  return best;
}

/**
 * Keep a fill whose cycle is still a face. When the cycle is gone, keep the
 * color on the new face that contains the original seed. An opened cycle,
 * whose seed is no longer inside any face, is dropped.
 */
export function reconcileFills(
  fills: readonly RegionFill[],
  faces: readonly PlanarFace[],
  points: readonly LngLat[],
): RegionFill[] {
  const byKey = new Map<string, PlanarFace>();
  for (const face of faces) byKey.set(face.key, face);
  const used = new Set<string>();
  const out: RegionFill[] = [];
  for (const fill of fills) {
    const key = fill.cycle.join(",");
    let face = byKey.get(key) ?? null;
    if (!face) face = faceAt(faces, points, fill.seed[0], fill.seed[1]);
    if (!face || used.has(face.key)) continue;
    used.add(face.key);
    out.push({
      cycle: face.cycle.slice(),
      color: fill.color,
      seed: [fill.seed[0], fill.seed[1]],
    });
  }
  return out;
}

export function sameRegions(a: readonly RegionFill[], b: readonly RegionFill[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const left = a[i];
    const right = b[i];
    if (!left || !right) return false;
    if (left.color !== right.color) return false;
    if (left.seed[0] !== right.seed[0] || left.seed[1] !== right.seed[1]) return false;
    if (left.cycle.length !== right.cycle.length) return false;
    for (let k = 0; k < left.cycle.length; k++) {
      if (left.cycle[k] !== right.cycle[k]) return false;
    }
  }
  return true;
}

export function cloneRegions(fills: readonly RegionFill[]): RegionFill[] {
  return fills.map((fill) => ({
    cycle: fill.cycle.slice(),
    color: fill.color,
    seed: [fill.seed[0], fill.seed[1]] as LngLat,
  }));
}

/** Drop cycles that no longer name a real ring. */
export function sanitizeRegions(value: unknown, pointCount: number): RegionFill[] {
  if (!Array.isArray(value)) return [];
  const out: RegionFill[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const raw = item as { cycle?: unknown; color?: unknown; seed?: unknown };
    if (!Array.isArray(raw.cycle) || raw.cycle.length < 3) continue;
    const color = typeof raw.color === "string" ? normalizeColor(raw.color) : null;
    if (!color) continue;
    if (!Array.isArray(raw.seed) || raw.seed.length < 2) continue;
    const lng = raw.seed[0];
    const lat = raw.seed[1];
    if (typeof lng !== "number" || typeof lat !== "number" || !Number.isFinite(lng) || !Number.isFinite(lat)) continue;
    const cycle: number[] = [];
    let ok = true;
    for (const id of raw.cycle) {
      if (typeof id !== "number" || !Number.isInteger(id) || id < 0 || id >= pointCount) {
        ok = false;
        break;
      }
      cycle.push(id);
    }
    if (!ok) continue;
    const key = cycle.join(",");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ cycle, color, seed: [lng, lat] });
  }
  return out;
}

const cursorCache = new Map<string, string>();
const CURSOR_CAP = 48;
/** Pour tip, in the 32px cursor bitmap. */
export const FILL_CURSOR_HOTSPOT = { x: 6, y: 28 } as const;

/**
 * One data-URL per color. Repeating a color reuses the same string, and the
 * cache forgets the oldest colors past {@link CURSOR_CAP}.
 */
export function fillCursor(color: string): string {
  const hex = color === NO_FILL ? NO_FILL : (normalizeColor(color) ?? "#3b82f6");
  const cached = cursorCache.get(hex);
  if (cached) {
    cursorCache.delete(hex);
    cursorCache.set(hex, cached);
    return cached;
  }
  const svg = hex === NO_FILL ? bucketSvg("#f4f4f5", true) : bucketSvg(hex, false);
  const url = `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${FILL_CURSOR_HOTSPOT.x} ${FILL_CURSOR_HOTSPOT.y}, crosshair`;
  cursorCache.set(hex, url);
  while (cursorCache.size > CURSOR_CAP) {
    const oldest = cursorCache.keys().next().value;
    if (oldest === undefined) break;
    cursorCache.delete(oldest);
  }
  return url;
}

export function segmentSignature(segments: ReadonlyArray<readonly [number, number]>): number {
  let hash = segments.length | 0;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    hash = (Math.imul(hash, 33) + Math.imul(seg?.[0] ?? 0, 131) + (seg?.[1] ?? 0)) | 0;
  }
  return hash;
}

function bucketSvg(color: string, slash: boolean): string {
  const mark = slash
    ? `<path d="M9 7l15 18" fill="none" stroke="#e11d48" stroke-width="1.8" stroke-linecap="round"/>`
    : "";
  const pour = slash ? "#d4d4d8" : color;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path d="M14 8h11l2 2.4V18l-2.2 2.4H13.2L12 18.2l3.4-6.6z" fill="${color}" stroke="#1a1c1e" stroke-width="1.35" stroke-linejoin="round"/><path d="M16 8c.2-3.2 6.2-3.2 6.4 0" fill="none" stroke="#1a1c1e" stroke-width="1.35"/><path d="M12.2 16.2L7.2 24.6" fill="none" stroke="${pour}" stroke-width="2.4" stroke-linecap="round"/><circle cx="6" cy="28" r="2.15" fill="${pour}" stroke="#1a1c1e" stroke-width="1.1"/>${mark}</svg>`;
}

/**
 * A point inside the face. The vertex average is tried first. When that lands
 * in a hole, the next try is a short step inward from an edge. Bounded faces
 * wind counter-clockwise, so the left of an edge is inside.
 */
function interiorSeed(
  points: readonly LngLat[],
  cycle: readonly number[],
  faces: readonly PlanarFace[],
): LngLat {
  const key = cycle.join(",");
  const ring: LngLat[] = [];
  for (const id of cycle) {
    const point = points[id];
    if (point) ring.push(point);
  }
  const fallback = ring[0] ?? [0, 0];
  if (ring.length === 0) return [fallback[0], fallback[1]];
  const candidates: LngLat[] = [];
  let lng = 0;
  let lat = 0;
  for (const point of ring) {
    lng += point[0];
    lat += point[1];
  }
  candidates.push([lng / ring.length, lat / ring.length]);
  const scales = [1e-8, 1e-7, 1e-6, 1e-5, 1e-4];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    if (!a || !b) continue;
    const midLng = (a[0] + b[0]) / 2;
    const midLat = (a[1] + b[1]) / 2;
    const cos = Math.cos((midLat * Math.PI) / 180) || 1;
    const dx = (b[0] - a[0]) * cos;
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    const leftLng = -dy / len / cos;
    const leftLat = dx / len;
    for (const scale of scales) candidates.push([midLng + leftLng * scale, midLat + leftLat * scale]);
  }
  for (const point of ring) candidates.push(point);
  for (const candidate of candidates) {
    if (!ringContains(points, cycle as number[], candidate[0], candidate[1])) continue;
    const hit = faceAt(faces, points, candidate[0], candidate[1]);
    if (hit?.key === key) return candidate;
  }
  return [fallback[0], fallback[1]];
}

function walkFace(adj: Half[][], used: boolean[][], startU: number, startE: number, limit: number): number[] {
  const cycle: number[] = [];
  let u = startU;
  let e = startE;
  for (let guard = 0; guard < limit; guard++) {
    if (used[u]?.[e]) break;
    const row = used[u];
    if (row) row[e] = true;
    cycle.push(u);
    const next = nextHalf(adj, u, e);
    if (!next) break;
    u = next.u;
    e = next.e;
    if (u === startU && e === startE) return cycle;
  }
  return cycle;
}

function nextHalf(adj: Half[][], u: number, e: number): { u: number; e: number } | null {
  const from = adj[u];
  const half = from?.[e];
  if (!half) return null;
  const into = adj[half.to];
  if (!into || into.length === 0) return null;
  let back = -1;
  for (let i = 0; i < into.length; i++) {
    if (into[i]?.to === u) {
      back = i;
      break;
    }
  }
  if (back < 0) return null;
  return { u: half.to, e: (back - 1 + into.length) % into.length };
}

function rotateMin(cycle: number[]): number[] {
  let minAt = 0;
  for (let i = 1; i < cycle.length; i++) {
    if ((cycle[i] ?? Infinity) < (cycle[minAt] ?? Infinity)) minAt = i;
  }
  return cycle.slice(minAt).concat(cycle.slice(0, minAt));
}

function angleBetween(from: LngLat, to: LngLat): number {
  const cos = Math.cos((from[1] * Math.PI) / 180);
  return Math.atan2(to[1] - from[1], (to[0] - from[0]) * cos);
}

function signedAreaM2(points: readonly LngLat[], cycle: number[]): number {
  const origin = points[cycle[0] ?? 0];
  if (!origin) return 0;
  const cos = Math.cos((origin[1] * Math.PI) / 180);
  let sum = 0;
  for (let i = 0; i < cycle.length; i++) {
    const a = points[cycle[i] ?? 0];
    const b = points[cycle[(i + 1) % cycle.length] ?? 0];
    if (!a || !b) return 0;
    const ax = (a[0] - origin[0]) * cos * METERS_PER_DEG;
    const ay = (a[1] - origin[1]) * METERS_PER_DEG;
    const bx = (b[0] - origin[0]) * cos * METERS_PER_DEG;
    const by = (b[1] - origin[1]) * METERS_PER_DEG;
    sum += ax * by - bx * ay;
  }
  return sum / 2;
}

function bboxOf(
  points: readonly LngLat[],
  cycle: number[],
): Pick<PlanarFace, "minLng" | "minLat" | "maxLng" | "maxLat"> {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const id of cycle) {
    const point = points[id];
    if (!point) continue;
    if (point[0] < minLng) minLng = point[0];
    if (point[1] < minLat) minLat = point[1];
    if (point[0] > maxLng) maxLng = point[0];
    if (point[1] > maxLat) maxLat = point[1];
  }
  return { minLng, minLat, maxLng, maxLat };
}

function ringContains(points: readonly LngLat[], cycle: number[], lng: number, lat: number): boolean {
  let inside = false;
  for (let i = 0, j = cycle.length - 1; i < cycle.length; j = i++) {
    const a = points[cycle[i] ?? 0];
    const b = points[cycle[j] ?? 0];
    if (!a || !b) return false;
    if (onSegment(a, b, lng, lat)) return true;
    const intersects = (a[1] > lat) !== (b[1] > lat)
      && lng < ((b[0] - a[0]) * (lat - a[1])) / (b[1] - a[1]) + a[0];
    if (intersects) inside = !inside;
  }
  return inside;
}

function onSegment(a: LngLat, b: LngLat, lng: number, lat: number): boolean {
  const cross = (lat - a[1]) * (b[0] - a[0]) - (lng - a[0]) * (b[1] - a[1]);
  if (Math.abs(cross) > 1e-12) return false;
  const dot = (lng - a[0]) * (b[0] - a[0]) + (lat - a[1]) * (b[1] - a[1]);
  if (dot < 0) return false;
  const len2 = (b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2;
  return dot <= len2;
}
